"use client";

import React, { useState } from "react";
import Link from "next/link";
import { t } from "@/lib/site-texts";

export default function HerroepingPage() {
  const [step, setStep] = useState<"info" | "form" | "success">("info");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    naam: "",
    email: "",
    adres: "",
    postcode: "",
    stad: "",
    bestelnummer: "",
    producten: "",
    ontvangstdatum: "",
  });

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    const verplichteVelden: { key: keyof typeof form; label: string }[] = [
      { key: "naam", label: "Naam" },
      { key: "email", label: "E-mailadres" },
      { key: "adres", label: "Straat + huisnummer" },
      { key: "postcode", label: "Postcode" },
      { key: "stad", label: "Woonplaats" },
      { key: "bestelnummer", label: "Bestelnummer" },
      { key: "ontvangstdatum", label: "Ontvangstdatum" },
      { key: "producten", label: "Te retourneren producten" },
    ];
    const ontbrekend = verplichteVelden.find((v) => !form[v.key].trim());
    if (ontbrekend) {
      setError(`Vul het verplichte veld "${ontbrekend.label}" in.`);
      return;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(form.email.trim())) {
      setError("Vul een geldig e-mailadres in.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/herroeping", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, datum: new Date().toISOString() }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Verzenden mislukt");
      }
      setStep("success");
    } catch (err: any) {
      setError(err.message || "Er ging iets mis. Probeer het opnieuw of stuur een e-mail.");
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d4d4d4",
    borderRadius: "4px",
    fontSize: "0.9rem",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    color: "#1a1a1a",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#555",
    marginBottom: "5px",
  };

  return (
    <div className="container" style={{ maxWidth: "680px", paddingTop: "50px", paddingBottom: "80px" }}>

      {/* Breadcrumb */}
      <div style={{ fontSize: "0.8rem", color: "#aaa", marginBottom: "28px" }}>
        <Link href="/" style={{ color: "inherit", textDecoration: "none" }}>Home</Link>
        {" / "}
        <span data-t-key="herroeping.breadcrumb" style={{ color: "var(--color-secondary)", fontWeight: 500 }}>{t("herroeping.breadcrumb", "Herroeping & annulering")}</span>
      </div>

      <h1 data-t-key="herroeping.h1" style={{ fontSize: "1.75rem", color: "var(--color-secondary)", marginBottom: "8px" }}>
        {t("herroeping.h1", "Herroeping & annulering")}
      </h1>
      <p style={{ color: "#666", fontSize: "0.92rem", marginBottom: "32px", lineHeight: 1.6 }}>
        <span data-t-key="herroeping.intro-1">{t("herroeping.intro-1", "U heeft het recht om uw aankoop binnen")}</span>{" "}
        <strong>14 dagen</strong>{" "}
        <span data-t-key="herroeping.intro-2">{t("herroeping.intro-2", "na ontvangst zonder opgaaf van reden te annuleren. Dit recht geldt op basis van de EU-consumentenrechtenrichtlijn en artikel 6:230o BW.")}</span>
      </p>

      {step === "info" && (
        <>
          {/* Wet-info blok */}
          <div style={{ background: "#f8f6f3", border: "1px solid #e8e3dc", borderRadius: "8px", padding: "24px 28px", marginBottom: "28px" }}>
            <h2 style={{ fontSize: "1rem", fontWeight: 700, color: "var(--color-secondary)", marginBottom: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span data-t-key="herroeping.info.heading">{t("herroeping.info.heading", "Uw herroepingsrecht")}</span>
            </h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: "10px", fontSize: "0.88rem", color: "#444", lineHeight: 1.55 }}>
              <li style={{ display: "flex", gap: "10px" }}>
                <span data-t-key="herroeping.info.item1-label" style={{ color: "var(--color-primary)", fontWeight: 700, flexShrink: 0 }}>{t("herroeping.info.item1-label", "14 dagen")}</span>
                <span data-t-key="herroeping.info.item1-body">{t("herroeping.info.item1-body", "Bedenktijd na ontvangst van uw bestelling (bij meerdere producten: na ontvangst van het laatste artikel).")}</span>
              </li>
              <li style={{ display: "flex", gap: "10px" }}>
                <span data-t-key="herroeping.info.item2-label" style={{ color: "var(--color-primary)", fontWeight: 700, flexShrink: 0 }}>{t("herroeping.info.item2-label", "Geen reden")}</span>
                <span data-t-key="herroeping.info.item2-body">{t("herroeping.info.item2-body", "U hoeft geen reden op te geven voor uw herroeping.")}</span>
              </li>
              <li style={{ display: "flex", gap: "10px" }}>
                <span data-t-key="herroeping.info.item3-label" style={{ color: "var(--color-primary)", fontWeight: 700, flexShrink: 0 }}>{t("herroeping.info.item3-label", "Terugbetaling")}</span>
                <span>
                  <span data-t-key="herroeping.info.item3-body-1">{t("herroeping.info.item3-body-1", "Wij betalen het aankoopbedrag incl. standaard verzendkosten binnen")}</span>{" "}
                  <strong>14 dagen</strong>{" "}
                  <span data-t-key="herroeping.info.item3-body-2">{t("herroeping.info.item3-body-2", "na ontvangst van uw herroeping terug (na ontvangst retour).")}</span>
                </span>
              </li>
              <li style={{ display: "flex", gap: "10px" }}>
                <span data-t-key="herroeping.info.item4-label" style={{ color: "var(--color-primary)", fontWeight: 700, flexShrink: 0 }}>{t("herroeping.info.item4-label", "Retour")}</span>
                <span>
                  <span data-t-key="herroeping.info.item4-body-1">{t("herroeping.info.item4-body-1", "Stuur de producten uiterlijk")}</span>{" "}
                  <strong>14 dagen</strong>{" "}
                  <span data-t-key="herroeping.info.item4-body-2">{t("herroeping.info.item4-body-2", "na uw herroeping ongebruikt en in originele verpakking terug.")}</span>
                </span>
              </li>
              <li style={{ display: "flex", gap: "10px" }}>
                <span data-t-key="herroeping.info.item5-label" style={{ color: "var(--color-primary)", fontWeight: 700, flexShrink: 0 }}>{t("herroeping.info.item5-label", "Uitzondering")}</span>
                <span data-t-key="herroeping.info.item5-body">{t("herroeping.info.item5-body", "Hygiëneproducten (ondergoed) waarvan de verzegeling is verbroken kunnen niet worden teruggestuurd.")}</span>
              </li>
            </ul>
          </div>

          {/* Nieuwe wetgeving 2026 notice */}
          <div style={{ background: "#eef6ff", border: "1px solid #c3ddf7", borderRadius: "8px", padding: "16px 20px", marginBottom: "28px", fontSize: "0.84rem", color: "#1a4a7a" }}>
            <strong data-t-key="herroeping.notice-label">{t("herroeping.notice-label", "Nieuw sinds 19 juni 2026:")}</strong>{" "}
            <span data-t-key="herroeping.notice-body">{t("herroeping.notice-body", "Wij zijn wettelijk verplicht een directe herroepingsknop aan te bieden. Gebruik de knop hieronder — u ontvangt direct een bevestiging per e-mail.")}</span>
          </div>

          {/* DE HERROEPINGSKNOP */}
          <button
            onClick={() => setStep("form")}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              width: "100%",
              padding: "16px 24px",
              background: "var(--color-primary)",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              fontSize: "1rem",
              fontWeight: 700,
              cursor: "pointer",
              letterSpacing: "0.02em",
              marginBottom: "16px",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="1,4 1,10 7,10"/><path d="M3.51 15a9 9 0 1 0 .49-3.96"/>
            </svg>
            <span data-t-key="herroeping.cta-button">{t("herroeping.cta-button", "Aankoop annuleren / herroepen")}</span>
          </button>

          <p style={{ textAlign: "center", fontSize: "0.78rem", color: "#aaa", marginBottom: "36px" }}>
            <span data-t-key="herroeping.cta-subtext">{t("herroeping.cta-subtext", "U ontvangt direct een bevestiging per e-mail · Geen account nodig")}</span>
          </p>

          {/* Alternatief: e-mail */}
          <div style={{ borderTop: "1px solid #eee", paddingTop: "24px" }}>
            <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "8px" }}>
              <span data-t-key="herroeping.email-instr">{t("herroeping.email-instr", "U kunt uw herroeping ook per e-mail melden aan:")}</span>{" "}
              <a href="mailto:info@mijnondergoedwinkel.nl" style={{ color: "var(--color-primary)", fontWeight: 600 }}>
                info@mijnondergoedwinkel.nl
              </a>
            </p>
            <p style={{ fontSize: "0.82rem", color: "#aaa" }}>
              <span data-t-key="herroeping.email-followup">{t("herroeping.email-followup", "Vermeld uw naam, bestelnummer en de producten die u wil retourneren. Wij bevestigen uw herroeping binnen 1 werkdag.")}</span>
            </p>
          </div>
        </>
      )}

      {step === "form" && (
        <form onSubmit={handleSubmit} noValidate>
          <div style={{ background: "#fff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "28px", marginBottom: "24px" }}>
            <h2 data-t-key="herroeping.form.heading" style={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--color-secondary)", marginBottom: "6px" }}>
              {t("herroeping.form.heading", "Modelformulier voor herroeping")}
            </h2>
            <p data-t-key="herroeping.form.intro" style={{ fontSize: "0.82rem", color: "#888", marginBottom: "22px" }}>
              {t("herroeping.form.intro", "Vul onderstaand formulier in. U ontvangt direct een bevestiging op uw e-mailadres.")}
            </p>

            <div style={{ display: "grid", gap: "16px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={labelStyle} htmlFor="herr-naam">Naam *</label>
                  <input id="herr-naam" required value={form.naam} onChange={set("naam")} placeholder="Uw volledige naam" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="herr-email">E-mailadres *</label>
                  <input id="herr-email" type="email" required value={form.email} onChange={set("email")} placeholder="u@voorbeeld.nl" style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={labelStyle} htmlFor="herr-adres">Straat + huisnummer *</label>
                <input id="herr-adres" required value={form.adres} onChange={set("adres")} placeholder="Voorbeeldstraat 12" style={inputStyle} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: "12px" }}>
                <div>
                  <label style={labelStyle} htmlFor="herr-postcode">Postcode *</label>
                  <input id="herr-postcode" required value={form.postcode} onChange={set("postcode")} placeholder="1234 AB" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="herr-stad">Woonplaats *</label>
                  <input id="herr-stad" required value={form.stad} onChange={set("stad")} placeholder="Amsterdam" style={inputStyle} />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={labelStyle} htmlFor="herr-bestelnr">Bestelnummer *</label>
                  <input id="herr-bestelnr" required value={form.bestelnummer} onChange={set("bestelnummer")} placeholder="bijv. ABC12345" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle} htmlFor="herr-datum">Ontvangstdatum *</label>
                  <input id="herr-datum" type="date" required value={form.ontvangstdatum} onChange={set("ontvangstdatum")} style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={labelStyle} htmlFor="herr-producten">Te retourneren producten *</label>
                <textarea
                  id="herr-producten"
                  required
                  rows={3}
                  value={form.producten}
                  onChange={set("producten")}
                  placeholder="bijv. 2× Cavello boxershort maat M, kleur zwart"
                  style={{ ...inputStyle, resize: "vertical" }}
                />
              </div>
            </div>
          </div>

          {/* Wettelijke verklaring */}
          <div data-t-key="herroeping.form.legal" style={{ background: "#f8f6f3", border: "1px solid #e8e3dc", borderRadius: "6px", padding: "14px 18px", marginBottom: "20px", fontSize: "0.8rem", color: "#555", lineHeight: 1.6 }}>
            {t("herroeping.form.legal", "Door dit formulier in te dienen verklaart u hierbij de overeenkomst voor de aankoop van bovenstaande producten te herroepen conform artikel 6:230o van het Burgerlijk Wetboek en de EU-consumentenrechtenrichtlijn (2011/83/EU).")}
          </div>

          {error && (
            <div role="alert" style={{ background: "#fdecea", border: "1px solid #f5c6c2", borderRadius: "4px", padding: "12px 16px", marginBottom: "16px", fontSize: "0.85rem", color: "#c0392b" }}>
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: "12px" }}>
            <button
              type="submit"
              disabled={submitting}
              style={{
                flex: 1, padding: "14px", background: "var(--color-primary)", color: "#fff",
                border: "none", borderRadius: "6px", fontSize: "0.95rem", fontWeight: 700,
                cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? "Verzenden…" : <span data-t-key="herroeping.form.submit">{t("herroeping.form.submit", "Herroeping indienen")}</span>}
            </button>
            <button
              type="button"
              onClick={() => setStep("info")}
              style={{
                padding: "14px 20px", background: "none", border: "1px solid #d4d4d4",
                borderRadius: "6px", fontSize: "0.88rem", color: "#555", cursor: "pointer",
              }}
            >
              <span data-t-key="herroeping.form.back">{t("herroeping.form.back", "Terug")}</span>
            </button>
          </div>
        </form>
      )}

      {step === "success" && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <div style={{ width: "64px", height: "64px", background: "#e8f5e9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1c7c3c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20,6 9,17 4,12"/>
            </svg>
          </div>
          <h2 data-t-key="herroeping.success.heading" style={{ fontSize: "1.4rem", color: "var(--color-secondary)", marginBottom: "10px" }}>
            {t("herroeping.success.heading", "Herroeping ontvangen")}
          </h2>
          <p style={{ color: "#555", fontSize: "0.92rem", marginBottom: "6px", lineHeight: 1.6, maxWidth: "460px", margin: "0 auto 8px" }}>
            <span data-t-key="herroeping.success.body-1">{t("herroeping.success.body-1", "Uw herroeping is geregistreerd. U ontvangt binnen enkele minuten een")}</span>{" "}
            <strong>bevestiging per e-mail</strong>.
          </p>
          <p data-t-key="herroeping.success.followup" style={{ color: "#888", fontSize: "0.84rem", marginBottom: "28px" }}>
            {t("herroeping.success.followup", "Stuur de producten binnen 14 dagen retour. Wij betalen u zo snel mogelijk terug.")}
          </p>
          <Link href="/" className="btn btn-outline" style={{ padding: "11px 24px" }}>
            <span data-t-key="herroeping.success.link">{t("herroeping.success.link", "Terug naar de winkel")}</span>
          </Link>
        </div>
      )}
    </div>
  );
}
